import {
  extractMentionUsernames,
  isEveryoneMention,
} from './mentionDisplayUtils';

/**
 * Strip HTML tags from message content (safe for notification previews).
 */
export function stripMessageHtml(html) {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  }
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Whether a message @mentions the given user (IDs from API or @username in text).
 */
export function isMessageMentioningUser(message, userId, username) {
  if (!message || userId == null || userId === '') return false;

  const normalizedUserId = String(userId);
  const ids = message.mentionedUserIds;
  if (Array.isArray(ids) && ids.some((id) => id != null && String(id) === normalizedUserId)) {
    return true;
  }

  const plain = stripMessageHtml(message.content || '');
  const tokens = extractMentionUsernames(plain).map((token) => token.toLowerCase());
  if (tokens.some((token) => isEveryoneMention(token))) {
    return true;
  }
  if (!username) return false;
  const needle = username.toLowerCase();
  return tokens.includes(needle);
}

/**
 * Latest unread message in a channel that mentions the user (newest first).
 */
export function getLastUnreadMentionInChannel(
  channelMessages,
  userId,
  username,
  lastViewedIso
) {
  if (!channelMessages?.length || !userId) return null;

  const lastViewedTime = lastViewedIso
    ? new Date(lastViewedIso).getTime()
    : 0;

  for (let i = channelMessages.length - 1; i >= 0; i--) {
    const msg = channelMessages[i];
    const msgTime = new Date(msg.createdAt || msg.timestamp || 0).getTime();
    const isFromOtherUser = msg.senderId !== userId;
    const isUnread = lastViewedTime === 0 || msgTime > lastViewedTime;

    if (isFromOtherUser && isUnread && isMessageMentioningUser(msg, userId, username)) {
      return msg;
    }
  }
  return null;
}

/**
 * Bubble title aligned with mobile FCM mention copy.
 */
export function formatMentionBubbleTitle(senderName, channelName, isDirectMessage) {
  const sender = senderName?.trim() || 'Someone';
  if (isDirectMessage) {
    return sender;
  }
  const channel = channelName?.trim() || 'Channel';
  return `${sender} mentioned you in ${channel}`;
}

/**
 * Plain-text preview for bubble body line.
 */
export function getMessagePreviewBody(message, maxLength = 60) {
  if (!message) return '';
  let preview = '';
  if (message.content) {
    preview = stripMessageHtml(message.content);
    if (preview.length > maxLength) {
      preview = `${preview.substring(0, maxLength)}...`;
    }
  } else if (message.attachments?.length > 0) {
    const n = message.attachments.length;
    preview = `${n} attachment${n > 1 ? 's' : ''}`;
  } else {
    preview = 'New message';
  }
  return preview;
}
